import { useCallback, useEffect, useRef } from 'react';

import useHotelReviewsCache from './useHotelReviewsCache';

import type { HotelReviewsState } from '../types/hotel-reviews';
import type { GetHotelReviewsResponse } from '@shared/types/frontend/ubff/hotel-reviews';
import type { UBFFGateway } from '@shared/types/frontend/ubff-gateway';
import type { Nullable } from '@shared/types/utils';

import { REVIEWS_PER_PAGE_DESKTOP } from '@hotels-hotel-detail/utils/review-service';

const useHotelReviewsFetch = (
  state: HotelReviewsState,
  setState: React.Dispatch<Partial<HotelReviewsState>>,
  uBFFGateway: UBFFGateway,
) => {
  const cacheManager = useHotelReviewsCache();

  const initializedRef = useRef(false);

  const fetchData = async (offset: number, limit: number) => {
    const errorHandledResponse = await uBFFGateway.getHotelReviews({
      hotelId: state.hotelId,
      limit,
      offset,
      locale: state.locale,
      sortType: state.sortType,
      filters: state.filters,
    });
    const { error, response: pbResponse } = errorHandledResponse;
    // Instead of using the Protobuf type directly,
    // we convert it to the redefined type of GetHotelReviewsResponse before use
    // to avoid unnecessary null/undefined checks.
    const response = pbResponse as Nullable<GetHotelReviewsResponse>;
    return { response, error };
  };

  const fetchDataDispatch = useCallback(
    async (offset: number, limit: number) => {
      const key = cacheManager.getCacheKey(
        offset,
        state.hotelId,
        state.filters!,
        state.sortType!,
      );
      const cached = cacheManager.getCachedData(key);
      if (cached) {
        setState({ data: cached, isLoading: false });
        return;
      }

      setState({ isLoading: true, error: undefined });

      const { error, response } = await fetchData(offset, limit);
      if (error) {
        setState({
          isLoading: false,
          error,
        });
        return;
      }
      if (response) {
        setState({
          isLoading: false,
          data: response,
        });
        cacheManager.updateCache(key, response);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.filters, state.offset, state.hotelId, state.sortType, state.locale],
  );

  useEffect(() => {
    const shouldFetch =
      state.filters !== undefined ||
      state.sortType !== undefined ||
      (state.offset !== undefined &&
        (!initializedRef.current || state.offset !== 0));

    if (shouldFetch) {
      fetchDataDispatch(state.offset ?? 0, REVIEWS_PER_PAGE_DESKTOP);
      initializedRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filters, state.offset, state.sortType]);

  return { fetchDataDispatch, fetchData };
};

export default useHotelReviewsFetch;
